import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import Modal from "../../components/Modal";
import DataTable from "../../components/DataTable";
import StudentPhoto from "../../components/StudentPhoto";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { listCashOnlyLedgers } from "../../api/accountApi";
import {
  getFeeCollectionView,
  getFeeCollectionViewByCustomStudentId,
  listAutoLedgerMappings,
  collectFee,
  listInvoicesForStudent,
  getMoneyReceipt,
} from "../../api/feeApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";
import "./FeeCollection.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const today = () => new Date().toISOString().slice(0, 10);
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentMonthName = () => MONTHS[new Date().getMonth()];

/**
 * Direct port of digicampusbd's two real "take a payment" screens — Fee
 * Collect (ID Wise) (`fee-collection-manual`, old h4: "Fees Collection") and
 * Fee Collect (Section Wise) (`section-roll-wise-fee-collection`) — branched
 * on `pageUrl` the same way {@code AdmitCardPage} branches on its own
 * `showRoutine`. A prior migration phase had pointed both URLs at a
 * simplified manual fee-entry form with none of the real search flow or
 * collect-section fields; this restores both, reading each student's actual
 * computed payable/due list from `GET /fee-invoices/collection-view[-by-
 * custom-id]` (see that DTO's javadoc for the one deliberate flattening it
 * makes) instead of requiring staff to type fee heads/amounts by hand.
 *
 * "Paid Details" / "Due Details" open the old app's own separate
 * `single-invoice-list` / `single-due-list` pop-out pages — here, an inline
 * modal instead (the due data is already loaded on this screen; the paid
 * data reuses the same `listInvoicesForStudent` call `FeeInvoiceListPage`
 * uses), rather than standing up two more single-purpose routes for data
 * this screen already has one click away.
 */
export default function FeeCollectionPage() {
  const { pageUrl } = useParams();
  const sectionWise = pageUrl === "section-roll-wise-fee-collection";

  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigurationId, setClassConfigurationId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [roster, setRoster] = useState([]);
  const [identificationId, setIdentificationId] = useState("");
  const [customStudentId, setCustomStudentId] = useState("");
  const [upToMonth, setUpToMonth] = useState(currentMonthName());

  const [view, setView] = useState(null);
  const [chargeGroups, setChargeGroups] = useState([]);
  const [dueRows, setDueRows] = useState([]);
  const [recurringLines, setRecurringLines] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [ledgers, setLedgers] = useState([]);
  const [autoLedgerMappings, setAutoLedgerMappings] = useState([]);
  const [paymentLedgerId, setPaymentLedgerId] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [note, setNote] = useState("");
  const [sendSms, setSendSms] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);

  const [showPaidDetails, setShowPaidDetails] = useState(false);
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [showDueDetails, setShowDueDetails] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    Promise.all([academicYearApi.list(), listClassConfigurations(), listCashOnlyLedgers(), listAutoLedgerMappings()]).then(
      ([years, configs, ledgerList, mappings]) => {
        setAcademicYears(years);
        if (years.length > 0) setAcademicYear(years[0].name);
        setClassConfigs(configs);
        if (configs.length > 0) setClassConfigurationId(String(configs[0].id));
        setLedgers(ledgerList);
        setAutoLedgerMappings(mappings);
      }
    );
    if (sectionWise) groupApi.list().then(setGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionWise]);

  useEffect(() => {
    if (groups.length > 0 && !groupId) setGroupId(String(groups[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  useEffect(() => {
    if (!sectionWise || !classConfigurationId || !academicYear) {
      setRoster([]);
      return;
    }
    listStudents(academicYear, classConfigurationId).then((list) => {
      const filtered = groupId ? list.filter((s) => String(s.groupId) === groupId) : list;
      setRoster(filtered.slice().sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0)));
    });
  }, [sectionWise, classConfigurationId, groupId, academicYear]);

  // Section-wise: auto-pick the class's mapped ledger, falling back to the first ledger —
  // direct port of digicampusbd's classconfigid-change handler + debitledgerlist() fallback.
  useEffect(() => {
    if (!sectionWise) return;
    const mapping = autoLedgerMappings.find((m) => String(m.classConfigurationId) === classConfigurationId);
    if (mapping) {
      setPaymentLedgerId(String(mapping.ledgerId));
    } else if (ledgers.length > 0) {
      setPaymentLedgerId(String(ledgers[0].ledgerId));
    }
  }, [sectionWise, classConfigurationId, autoLedgerMappings, ledgers]);

  // ID-wise: digicampusbd's own fee-collection-manual page just lists every ledger, no class-based auto-mapping.
  useEffect(() => {
    if (sectionWise || paymentLedgerId) return;
    if (ledgers.length > 0) setPaymentLedgerId(String(ledgers[0].ledgerId));
  }, [sectionWise, ledgers, paymentLedgerId]);

  useEffect(() => {
    setView(null);
    setChargeGroups([]);
    setDueRows([]);
    setRecurringLines([]);
    setLastInvoice(null);
    setError("");
  }, [identificationId, customStudentId]);

  /**
   * Groups the flattened per-(feeHead, feeSubHead) rows the backend returns
   * (see FeeCollectionViewResponse's own javadoc on why it's flattened) back
   * into one row per fee head — direct fidelity to digicampusbd's real Fee
   * List table, which shows one row per head with a sub-head multiselect
   * inside it (all sub-heads checked by default), not one row per sub-head.
   */
  const buildChargeGroups = (feeHeadDetails) => {
    const byHead = new Map();
    feeHeadDetails.forEach((r) => {
      if (!byHead.has(r.feeHeadId)) {
        byHead.set(r.feeHeadId, {
          feeHeadId: r.feeHeadId,
          feeHeadName: r.feeHeadName,
          feeWaiverList: r.feeWaiverList,
          waiverId: r.feeWaiverId ? String(r.feeWaiverId) : "",
          paidAmount: "",
          selected: false,
          subHeads: [],
        });
      }
      byHead.get(r.feeHeadId).subHeads.push({
        feeSubHeadId: r.feeSubHeadId,
        feeSubHeadName: r.feeSubHeadName,
        payableMonth: r.payableMonth,
        feeAmount: r.feeAmount,
        fineAmount: r.fineAmount,
        waiverAmount: r.waiverAmount,
        checked: true,
      });
    });
    return Array.from(byHead.values()).map((g) => ({
      ...g,
      paidAmount: String(g.subHeads.reduce((s, sh) => s + sh.feeAmount + sh.fineAmount - sh.waiverAmount, 0)),
    }));
  };

  const applyView = (data) => {
    setView(data);
    setChargeGroups(buildChargeGroups(data.feeHeadDetails));
    setDueRows(
      data.previousDueDetails.map((r) => ({
        ...r,
        key: `due-${r.feeHeadId}-${r.feeSubHeadId}`,
        selected: false,
        paidAmount: String(r.previousDuePayable),
        waiverId: "",
      }))
    );
    if (data.feeHeadDetails.length === 0 && data.previousDueDetails.length === 0) {
      setError("Payable amount is empty");
    }
  };

  const loadView = async () => {
    setLoading(true);
    setError("");
    try {
      const data = sectionWise
        ? await getFeeCollectionView(identificationId, academicYear)
        : await getFeeCollectionViewByCustomStudentId(customStudentId, academicYear);
      applyView(data);
    } catch (err) {
      setView(null);
      setChargeGroups([]);
      setDueRows([]);
      setError((err.response && err.response.data && err.response.data.message) || "No Student Found.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSuccess("");
    setLastInvoice(null);
    setRecurringLines([]);
    if (sectionWise) {
      if (!identificationId) {
        setError("Please insert student id");
        return;
      }
    } else if (!customStudentId.trim()) {
      setError("Please insert student id");
      return;
    }
    loadView();
  };

  const recurringOptions = useMemo(() => {
    const opts = [];
    (view?.recurringVariableFees || []).forEach((head) => {
      head.feeSubHeadDetails.forEach((sub) => {
        opts.push({
          value: `${head.feeHeadId}-${sub.feeSubHeadId}`,
          feeHeadId: head.feeHeadId,
          feeSubHeadId: sub.feeSubHeadId,
          label: `${head.feeHeadName} - ${sub.feeSubHeadName}`,
        });
      });
    });
    return opts;
  }, [view]);

  const visibleChargeGroups = useMemo(() => {
    if (!sectionWise) return chargeGroups;
    const cutoff = MONTHS.indexOf(upToMonth);
    const allowedMonths = cutoff === -1 ? MONTHS : MONTHS.slice(0, cutoff + 1);
    return chargeGroups
      .map((g) => ({
        ...g,
        subHeads: g.subHeads.filter((sh) => !sh.payableMonth || allowedMonths.includes(sh.payableMonth)),
      }))
      .filter((g) => g.subHeads.length > 0);
  }, [chargeGroups, sectionWise, upToMonth]);

  /** Sums a fee-head group's checked sub-heads — direct port of digicampusbd's own `$('#tbl').on('change','select', ...)` aggregation. */
  const groupTotals = (group) => {
    const checked = group.subHeads.filter((sh) => sh.checked);
    const feeAmount = checked.reduce((s, sh) => s + sh.feeAmount, 0);
    const fineAmount = checked.reduce((s, sh) => s + sh.fineAmount, 0);
    const waiverAmount = checked.reduce((s, sh) => s + sh.waiverAmount, 0);
    return { checked, feeAmount, fineAmount, waiverAmount, payable: feeAmount + fineAmount - waiverAmount };
  };

  const setGroupField = (feeHeadId, field, value) =>
    setChargeGroups((prev) => prev.map((g) => (g.feeHeadId === feeHeadId ? { ...g, [field]: value } : g)));

  /** Toggling which sub-heads count resets Paid Now back to the new payable — matching the old app's own reset-on-change. */
  const toggleSubHead = (feeHeadId, feeSubHeadId, checked) =>
    setChargeGroups((prev) =>
      prev.map((g) => {
        if (g.feeHeadId !== feeHeadId) return g;
        const subHeads = g.subHeads.map((sh) => (sh.feeSubHeadId === feeSubHeadId ? { ...sh, checked } : sh));
        const { payable } = groupTotals({ ...g, subHeads });
        return { ...g, subHeads, paidAmount: String(payable) };
      })
    );

  const setDueField = (key, field, value) =>
    setDueRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

  const addRecurringLine = () => setRecurringLines((prev) => [...prev, { key: Math.random(), value: "", paidAmount: "" }]);
  const removeRecurringLine = (key) => setRecurringLines((prev) => prev.filter((l) => l.key !== key));
  const setRecurringField = (key, field, value) =>
    setRecurringLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));

  const selectedGroups = visibleChargeGroups.filter((g) => g.selected).map((g) => ({ ...g, totals: groupTotals(g) }));
  const selectedDues = dueRows.filter((r) => r.selected);
  const usableRecurring = recurringLines
    .map((l) => ({ ...l, option: recurringOptions.find((o) => o.value === l.value) }))
    .filter((l) => l.option && Number(l.paidAmount) > 0);

  const totalPayable =
    selectedGroups.reduce((sum, g) => sum + g.totals.payable, 0) +
    selectedDues.reduce((sum, r) => sum + r.previousDuePayable, 0) +
    usableRecurring.reduce((sum, l) => sum + Number(l.paidAmount), 0);
  const totalPaid =
    selectedGroups.reduce((sum, g) => sum + (Number(g.paidAmount) || 0), 0) +
    selectedDues.reduce((sum, r) => sum + (Number(r.paidAmount) || 0), 0) +
    usableRecurring.reduce((sum, l) => sum + Number(l.paidAmount), 0);
  const totalDue = totalPayable - totalPaid;

  const handleCollect = async () => {
    setError("");
    setSuccess("");
    if (!view) return;
    if (!paymentLedgerId) {
      setError("Please select a ledger");
      return;
    }
    if (totalPaid <= 0) {
      setError("Total paid should be more than zero");
      return;
    }

    const lines = [];
    // Direct port of digicampusbd's own ManualFeeCollectionService.collectFee arithmetic: a fee-head row's
    // totals (computed across every checked sub-head) are divided evenly across those same checked sub-heads
    // to produce one FeeInvoiceDetails-equivalent line each — same math, computed here instead of server-side
    // since this app's collect endpoint already wants one line per (feeHead, feeSubHead).
    selectedGroups.forEach((g) => {
      const n = g.totals.checked.length;
      if (n === 0) return;
      const paidTotal = Number(g.paidAmount) || 0;
      g.totals.checked.forEach((sh) => {
        lines.push({
          feeHeadId: g.feeHeadId,
          feeSubHeadId: sh.feeSubHeadId,
          duePayment: false,
          payableAmount: g.totals.payable / n,
          paidAmount: paidTotal / n,
          fineAmount: g.totals.fineAmount / n,
          waiverId: g.waiverId ? Number(g.waiverId) : null,
          waiverAmount: g.totals.waiverAmount / n,
        });
      });
    });
    selectedDues.forEach((r) => {
      lines.push({
        feeHeadId: r.feeHeadId,
        feeSubHeadId: r.feeSubHeadId,
        duePayment: true,
        requiredAmount: r.previousDuePayable,
        paidAmount: Number(r.paidAmount) || 0,
        waiverId: r.waiverId ? Number(r.waiverId) : null,
        waiverAmount: 0,
      });
    });
    usableRecurring.forEach((l) => {
      lines.push({
        feeHeadId: l.option.feeHeadId,
        feeSubHeadId: l.option.feeSubHeadId,
        duePayment: false,
        payableAmount: Number(l.paidAmount),
        paidAmount: Number(l.paidAmount),
        fineAmount: 0,
        waiverId: null,
        waiverAmount: 0,
      });
    });

    if (lines.length === 0) {
      setError("Please select at least one fee item to collect.");
      return;
    }

    setCollecting(true);
    try {
      const invoice = await collectFee({
        identificationId: view.identificationId,
        paymentDate,
        paymentLedgerId: Number(paymentLedgerId),
        academicYear,
        note,
        sendSms,
        lines,
      });
      setLastInvoice(invoice);
      setSuccess(`Fee Invoice ${invoice.invoiceId} Successfully Done.`);
      setRecurringLines([]);
      setNote("");
      await loadView();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to collect fee.");
    } finally {
      setCollecting(false);
    }
  };

  const openReceipt = async () => {
    if (!lastInvoice) return;
    try {
      setReceipt(await getMoneyReceipt(lastInvoice.masterId));
    } catch (err) {
      setError("Failed to load receipt.");
    }
  };

  const openPaidDetails = async () => {
    if (!view) return;
    setShowPaidDetails(true);
    try {
      setPaidInvoices(await listInvoicesForStudent(view.identificationId));
    } catch (err) {
      setPaidInvoices([]);
    }
  };

  const dueListSection = dueRows.length > 0 && (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <h4>Due List</h4>
      <table className="attendance-table">
        <thead>
          <tr>
            <th>Fee Head</th>
            <th>Fee SubHead</th>
            <th>Previous Due Payable</th>
            <th>Waiver</th>
            <th>Paid</th>
            <th>Due</th>
            <th>
              <input
                type="checkbox"
                onChange={(e) => setDueRows((prev) => prev.map((r) => ({ ...r, selected: e.target.checked })))}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {dueRows.map((r) => {
            const due = r.previousDuePayable - (Number(r.paidAmount) || 0);
            return (
              <tr key={r.key}>
                <td>{r.feeHeadName}</td>
                <td>{r.feeSubHeadName}</td>
                <td>{r.previousDuePayable}</td>
                <td>
                  <select value={r.waiverId} onChange={(e) => setDueField(r.key, "waiverId", e.target.value)}>
                    <option value="">None</option>
                    {(r.feeWaiverList || []).map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    style={{ width: 90 }}
                    value={r.paidAmount}
                    onChange={(e) => setDueField(r.key, "paidAmount", e.target.value)}
                  />
                </td>
                <td>{due}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={r.selected}
                    onChange={(e) => setDueField(r.key, "selected", e.target.checked)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const feeListSection = visibleChargeGroups.length > 0 && (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <h4>Fee List</h4>
      <table className="attendance-table">
        <thead>
          <tr>
            <th>Fee Head</th>
            <th>Fee Sub Head</th>
            <th>Waiver Type</th>
            <th>Fine Amount</th>
            <th>Fee Amount</th>
            <th>Waiver Amount</th>
            <th>Payable</th>
            <th>Paid Now</th>
            <th>Due Now</th>
            <th>
              <input
                type="checkbox"
                onChange={(e) =>
                  setChargeGroups((prev) => prev.map((g) => ({ ...g, selected: e.target.checked })))
                }
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {visibleChargeGroups.map((g) => {
            const totals = groupTotals(g);
            const due = totals.payable - (Number(g.paidAmount) || 0);
            return (
              <tr key={g.feeHeadId}>
                <td>{g.feeHeadName}</td>
                <td className="fee-collection-subhead-cell">
                  {g.subHeads.map((sh) => (
                    <label key={sh.feeSubHeadId} className="fee-collection-subhead-option">
                      <input
                        type="checkbox"
                        checked={sh.checked}
                        onChange={(e) => toggleSubHead(g.feeHeadId, sh.feeSubHeadId, e.target.checked)}
                      />
                      {sh.feeSubHeadName}
                      {sh.payableMonth ? ` (${sh.payableMonth})` : ""}
                    </label>
                  ))}
                </td>
                <td>
                  <select value={g.waiverId} onChange={(e) => setGroupField(g.feeHeadId, "waiverId", e.target.value)}>
                    <option value="">None</option>
                    {(g.feeWaiverList || []).map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{totals.fineAmount}</td>
                <td>{totals.feeAmount}</td>
                <td>{totals.waiverAmount}</td>
                <td>{totals.payable}</td>
                <td>
                  <input
                    type="number"
                    style={{ width: 90 }}
                    value={g.paidAmount}
                    onChange={(e) => setGroupField(g.feeHeadId, "paidAmount", e.target.value)}
                  />
                </td>
                <td>{due}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={g.selected}
                    onChange={(e) => setGroupField(g.feeHeadId, "selected", e.target.checked)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{sectionWise ? "Fee Collect (Section Wise)" : "Fees Collection"}</h1>
        {!sectionWise && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => window.open("/student-report-basic-info-list", "_blank")}
          >
            See Student List
          </button>
        )}
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {sectionWise ? (
        <>
          <div className="attendance-filters">
            <label>
              Select Year
              <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Section
              <select value={classConfigurationId} onChange={(e) => setClassConfigurationId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Select Group
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="attendance-filters">
            <label>
              Select Roll
              <select value={identificationId} onChange={(e) => setIdentificationId(e.target.value)}>
                <option value="">Student Roll</option>
                {roster.map((s) => (
                  <option key={s.identificationId} value={s.identificationId}>
                    {s.studentRoll} - {s.studentName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Up To Month
              <select value={upToMonth} onChange={(e) => setUpToMonth(e.target.value)}>
                {MONTHS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment Date
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </label>
          </div>
          <div className="attendance-filters fee-collection-actions-row">
            <button type="button" className="btn-secondary" disabled={!view} onClick={openPaidDetails}>
              Paid Details
            </button>
            <button type="button" className="btn-secondary" disabled={!view} onClick={() => setShowDueDetails(true)}>
              Due Details
            </button>
            <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </>
      ) : (
        <div className="attendance-filters">
          <label>
            Student ID
            <input
              value={customStudentId}
              onChange={(e) => setCustomStudentId(e.target.value)}
              placeholder="Enter Student Id"
            />
          </label>
          <label>
            Select Year
            <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
              {academicYears.map((y) => (
                <option key={y.id} value={y.name}>
                  {y.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            &nbsp;
            <button type="button" className="btn-primary" onClick={handleSearch} disabled={loading}>
              {loading ? "Searching..." : "Search"}
            </button>
          </label>
        </div>
      )}

      {view && (
        <>
          <div className="fee-collection-student-card">
            <StudentPhoto className="fee-collection-photo" fileName={view.imageName} alt={view.studentName} />
            <table className="attendance-table fee-collection-student-table">
              <tbody>
                <tr>
                  <th>Student Id</th>
                  <td>{view.customStudentId}</td>
                  <th>Student Name</th>
                  <td>{view.studentName}</td>
                </tr>
                <tr>
                  <th>Student Roll</th>
                  <td>{view.studentRoll}</td>
                  <th>Section</th>
                  <td>{view.section}</td>
                </tr>
                {sectionWise && (
                  <tr>
                    <th>Group</th>
                    <td>{view.group || "-"}</td>
                    <th>Category</th>
                    <td>{view.category || "-"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* digicampusbd's own two real templates disagree on this order — section-roll-wise-fee-collection.html
              shows Due List then Fee List, fee-collection-manual.html shows Fee List then Due List — preserved
              exactly rather than picking one order for both. */}
          {sectionWise ? (
            <>
              {dueListSection}
              {feeListSection}
            </>
          ) : (
            <>
              {feeListSection}
              {dueListSection}
            </>
          )}

          {sectionWise && recurringOptions.length > 0 && (
            <div className="table-scroll" style={{ marginTop: 16 }}>
              <h4>Recurring Variable Fees</h4>
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Fee Head</th>
                    <th>Paid Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recurringLines.map((l) => (
                    <tr key={l.key}>
                      <td>
                        <select value={l.value} onChange={(e) => setRecurringField(l.key, "value", e.target.value)}>
                          <option value="">Select Fee Head</option>
                          {recurringOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          style={{ width: 100 }}
                          value={l.paidAmount}
                          onChange={(e) => setRecurringField(l.key, "paidAmount", e.target.value)}
                        />
                      </td>
                      <td>
                        <button type="button" className="btn-link danger" onClick={() => removeRecurringLine(l.key)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn-link" onClick={addRecurringLine}>
                + Add Row
              </button>
            </div>
          )}

          <div className="fee-collection-collect-section">
            {/* Field order matches digicampusbd's real collectSection row exactly (col-md-2 x 6): Total
                Payable/Paid/Due, Payment Date, Paid By, Note — confirmed by running the old app live
                against the same database and reading its own rendered order. */}
            <div className="attendance-filters">
              <label>
                Total Payable
                <input type="number" value={totalPayable.toFixed(2)} disabled />
              </label>
              <label>
                Total Paid
                <input type="number" value={totalPaid.toFixed(2)} disabled />
              </label>
              <label>
                Total Due
                <input type="number" value={totalDue.toFixed(2)} disabled />
              </label>
              {!sectionWise && (
                <label>
                  Payment Date
                  <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                </label>
              )}
              <label>
                Paid By
                <select value={paymentLedgerId} onChange={(e) => setPaymentLedgerId(e.target.value)}>
                  {ledgers.map((l) => (
                    <option key={l.ledgerId} value={l.ledgerId}>
                      {l.ledgerName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Note
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Enter Note" />
              </label>
            </div>
            <div className="form-actions" style={{ justifyContent: "flex-end", alignItems: "center" }}>
              <label style={{ marginRight: 12 }}>
                <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} /> Send Sms
              </label>
              {lastInvoice && (
                <button type="button" className="btn-secondary" style={{ marginRight: 8 }} onClick={openReceipt}>
                  Download Receipt
                </button>
              )}
              <button type="button" className="btn-primary" onClick={handleCollect} disabled={collecting}>
                {collecting ? "Processing..." : "Collect"}
              </button>
            </div>
          </div>
        </>
      )}

      {showPaidDetails && (
        <Modal title={`Paid Details — ${view?.studentName || ""}`} onClose={() => setShowPaidDetails(false)}>
          <DataTable
            columns={[
              { key: "invoiceId", label: "Invoice #" },
              { key: "paymentDate", label: "Payment Date" },
              { key: "payableAmount", label: "Payable" },
              { key: "paidAmount", label: "Paid" },
              { key: "dueAmount", label: "Due" },
              { key: "username", label: "Collected By" },
            ]}
            rows={paidInvoices}
          />
        </Modal>
      )}

      {showDueDetails && (
        <Modal title={`Due Details — ${view?.studentName || ""}`} onClose={() => setShowDueDetails(false)}>
          <DataTable
            columns={[
              { key: "feeHeadName", label: "Fee Head" },
              { key: "feeSubHeadName", label: "Fee SubHead" },
              { key: "previousDuePayable", label: "Previous Due Payable" },
            ]}
            rows={view?.previousDueDetails || []}
          />
        </Modal>
      )}

      {receipt && (
        <Modal
          title={`Money Receipt — ${receipt.invoiceId}`}
          onClose={() => setReceipt(null)}
          footer={
            <button type="button" className="btn-primary" onClick={() => window.print()}>
              Print
            </button>
          }
        >
          <p>
            <strong>{receipt.studentName}</strong> ({receipt.customStudentId}) — {receipt.className}, Roll {receipt.roll}
          </p>
          <p>
            Payment Date: {receipt.paymentDate} &nbsp; By: {receipt.username}
          </p>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Fee Head</th>
                  <th>Sub-Head</th>
                  <th>Payable</th>
                  <th>Waiver</th>
                  <th>Fine</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {receipt.lines.map((l, idx) => (
                  <tr key={idx}>
                    <td>{l.feeHeadName}</td>
                    <td>{l.feeSubHeadName}</td>
                    <td>{l.payable}</td>
                    <td>{l.waiver}</td>
                    <td>{l.fine}</td>
                    <td>{l.paid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            <strong>Total Paid: {receipt.totalPaid}</strong> ({receipt.paidInWord})
          </p>
          {receipt.note && <p>Note: {receipt.note}</p>}
        </Modal>
      )}
    </MainLayout>
  );
}
